import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
} from 'react';
import {
  AppRole,
  UserRole,
  ROLE_PERMISSIONS,
  RolePermissions,
} from '@xanectech/types';
import { supabase } from '@xanectech/supabase-client';
import { useAuth } from '../contexts/AuthContext';

import { createLogger } from '@xanectech/lib';
import { isRecord } from '@xanectech/lib';

const logger = createLogger('Auth - RoleContext');

const readString = (
  value: Record<string, unknown>,
  key: string,
): string | undefined =>
  typeof value[key] === 'string' ? value[key] : undefined;

const readBoolean = (
  value: Record<string, unknown>,
  key: string,
): boolean | undefined =>
  typeof value[key] === 'boolean' ? value[key] : undefined;

const APP_ROLES: AppRole[] = [
  'super_admin',
  'internal_admin',
  'company_admin',
  'data_administrator',
  'viewer',
  'end_user',
];

const isAppRole = (value: unknown): value is AppRole =>
  typeof value === 'string' && APP_ROLES.includes(value as AppRole);

const normalizeUserRole = (row: unknown): UserRole | null => {
  if (!isRecord(row)) {
    return null;
  }

  const id = readString(row, 'id');
  const userId = readString(row, 'user_id');
  const roleValue = row.role;

  if (!id || !userId || !isAppRole(roleValue)) {
    return null;
  }

  const nowIso = new Date().toISOString();

  return {
    id,
    user_id: userId,
    role: roleValue,
    assigned_by: readString(row, 'assigned_by'),
    company_id: readString(row, 'company_id'),
    is_active: readBoolean(row, 'is_active') ?? true,
    assigned_at: readString(row, 'assigned_at') || nowIso,
    created_at: readString(row, 'created_at') || nowIso,
    updated_at: readString(row, 'updated_at') || nowIso,
    division_id: readString(row, 'division_id'),
  };
};

interface RoleContextType {
  userRoles: UserRole[];
  currentRole: AppRole | null;
  permissions: RolePermissions;
  loading: boolean;

  switchRole: (role: AppRole) => void;

  hasRole: (role: AppRole) => boolean;

  hasPermission: (permission: keyof RolePermissions) => boolean;
  refreshRoles: () => Promise<void>;
}

const RoleContext = createContext<RoleContextType | undefined>(undefined);

export const useRole = () => {
  const context = useContext(RoleContext);

  if (context === undefined) {
    throw new Error('useRole must be used within a RoleProvider');
  }
  return context;
};

interface RoleProviderProps {
  children: ReactNode;
}

export const RoleProvider: React.FC<RoleProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [userRoles, setUserRoles] = useState<UserRole[]>([]);
  const [currentRole, setCurrentRole] = useState<AppRole | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshRoles = useCallback(async () => {
    if (!user) {
      setUserRoles([]);
      setCurrentRole(null);
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_active', true);

      if (error) {
        logger.error('Error fetching user roles:', error);
        setUserRoles([]);
        setCurrentRole(null);
      } else {
        const roles = (Array.isArray(data) ? data : [])
          .map(normalizeUserRole)
          .filter((role): role is UserRole => role !== null);
        setUserRoles(roles);

        // Set current role to the highest privilege role or first available
        if (roles.length > 0) {
          const primaryRole = roles.reduce((highest, current) => {
            const roleHierarchy: Record<AppRole, number> = {
              super_admin: 1,
              internal_admin: 2,
              company_admin: 3,
              data_administrator: 4,
              viewer: 5,
              end_user: 6,
            };

            return roleHierarchy[current.role] < roleHierarchy[highest.role]
              ? current
              : highest;
          });

          setCurrentRole(primaryRole.role);
        } else {
          setCurrentRole(null);
        }
      }
    } catch (error) {
      logger.error('Error in refreshRoles:', error as Error);
      setUserRoles([]);
      setCurrentRole(null);
    }

    setLoading(false);
  }, [user]);

  useEffect(() => {
    if (user?.id) {
      void refreshRoles();
    } else {
      // Clear role data when user logs out - also clear any stored role data
      setUserRoles([]);
      setCurrentRole(null);
      // Remove any cached role data from storage - for security
      const userStorageKeys = Object.keys(localStorage).filter(
        (key) =>
          key.includes('user_roles') ||
          key.includes('current_role') ||
          key.includes('role'),
      );
      userStorageKeys.forEach((key) => localStorage.removeItem(key));

      const sessionStorageKeys = Object.keys(sessionStorage).filter(
        (key) =>
          key.includes('user_roles') ||
          key.includes('current_role') ||
          key.includes('role'),
      );
      sessionStorageKeys.forEach((key) => sessionStorage.removeItem(key));
    }
  }, [user, refreshRoles]);

  const switchRole = (role: AppRole) => {
    if (hasRole(role)) {
      setCurrentRole(role);
      // Don't store current role in localStorage for security - use memory only
    } else {
      logger.warn(`User attempted to switch to unauthorized role: ${role}`);
    }
  };

  const hasRole = (role: AppRole): boolean => {
    return userRoles.some((userRole) => userRole.role === role);
  };

  const hasPermission = (permission: keyof RolePermissions): boolean => {
    if (!currentRole) return false;
    return ROLE_PERMISSIONS[currentRole][permission];
  };

  const permissions = currentRole
    ? ROLE_PERMISSIONS[currentRole]
    : ROLE_PERMISSIONS['end_user'];

  const value: RoleContextType = {
    userRoles,
    currentRole,
    permissions,
    loading,
    switchRole,
    hasRole,
    hasPermission,
    refreshRoles,
  };

  return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>;
};
